"use client";
import FilterTag from "@components/atoms/FilterTag";
import React, { useState, useEffect } from "react";
import styles from "./FilterToggle.module.scss";
import Button from "@components/atoms/Button";
import SortModal from "@components/molecules/SortModal";
import ModalManager from "../ModalManager";
import { cn } from "@utils/utils";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { formatPrice } from "@utils/numberFormatter";

type FilterConfig = {
  key: string;
  label: string;
  prefix?: string;
  modalTitle: string;
  options?: string[];
  multiSelect?: boolean;
  withPriceRange?: boolean;
  withClear?: boolean;
  maxPrice?: number; // Optional, used only if withPriceRange is true
};

interface FilterToggleProps {
  options?: string[];
  filterConfigs?: FilterConfig[];
  onApply?: (key: string, selectedValues: string[]) => void;
  onClear?: () => void;
  initialSelectedFilters?: Record<string, string[]>;
  wrapperClassName?: string;
  pageType?: string;
  brandName?: string;
  modelName?: string;
  carAvailability?: string;
}

export default function FilterToggle({
  options,
  filterConfigs,
  onApply,
  onClear,
  initialSelectedFilters = {},
  wrapperClassName,
  pageType,
  brandName,
  modelName,
  carAvailability,
}: FilterToggleProps) {
  const { name: cityName } = getSelectedCityClient();
  const [selected, setSelected] = useState(options?.[0]);
  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);
  const [selectedMap, setSelectedMap] = useState<Record<string, string[]>>({});
  const [applyingFilterKey, setApplyingFilterKey] = useState<string | null>(null);

  // Sync selectedMap with initialSelectedFilters from parent
  useEffect(() => {
    setSelectedMap(initialSelectedFilters);
  }, [initialSelectedFilters]);

  const getSelectedValues = (key: string) => selectedMap[key] || [];

  const hasSelectedValues = (key: string) => getSelectedValues(key).length > 0;

  const hasAnySelectedFilter = Object.values(selectedMap).some(
    (values) => values.length > 0
  );

  const formatSelectedValue = (key: string, values: string[]) => {
    if (values.length === 0) {
      return "";
    }

    if (key === "priceRange" && values[0]?.includes("-")) {
      const [minPrice, maxPrice] = values[0].split("-").map(Number);

      if (Number.isFinite(minPrice) && Number.isFinite(maxPrice)) {
        return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
      }
    }

    if (values.length === 1) {
      return values[0];
    }

    return `${values.length} selected`;
  };

  const getFilterTagLabel = (filter: FilterConfig) => {
    const selectedValues = getSelectedValues(filter.key);
    const selectedLabel = formatSelectedValue(filter.key, selectedValues);

    return selectedLabel || filter.label;
  };

  const handleApply = async (key: string, values: string[]) => {
    setApplyingFilterKey(key);

    try {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType || "",
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_filter",
          selected_value: values.join(", "),
          filter_type: key,
          car_availability: carAvailability || "",
          vehicle_make: brandName || "",
          vehicle_model: modelName || "",
          city_name: cityName || "",
        },
      });

      setSelectedMap((prev) => ({ ...prev, [key]: values }));
      onApply?.(key, values);
      setActiveFilterKey(null);
    } finally {
      setApplyingFilterKey(null);
    }
  };

  const handleClear = () => {
    setSelected(options?.[0]);
    setSelectedMap({});
    onClear?.();
    setActiveFilterKey(null);
  };

  const activeFilter = filterConfigs?.find((f) => f.key === activeFilterKey);

  return (
    <div
      className={cn(
        styles.wrapper,
        wrapperClassName ? ` ${wrapperClassName}` : ""
      )}
    >
      <div className={styles.inner}>
        {/* Toggle Filter (KA/BH) */}
        {options && (
          <div className={styles.toggleGroup}>
            {options.map((option) => (
              <Button
                key={option}
                onClick={() => setSelected(option)}
                className={`${styles.toggleButton} ${
                  selected === option ? styles.active : ""
                }`}
              >
                {option}
              </Button>
            ))}
          </div>
        )}

        {/* Other Filters */}
        {/* Dynamic Filter Tags */}
        {filterConfigs?.map((filter) => {
          const isFilterActive = hasSelectedValues(filter.key);

          return (
            <FilterTag
              key={filter.key}
              label={getFilterTagLabel(filter)}
              prefix={isFilterActive ? filter.label + ":" : filter.prefix}
              onClick={() => setActiveFilterKey(filter.key)}
              isActive={isFilterActive}
            />
          );
        })}
        {(hasAnySelectedFilter || selected !== options?.[0]) && (
          <Button
            onClick={handleClear}
            className={styles.toggleButton}
            variant="secondary"
            type="button"
          >
            Clear
          </Button>
        )}
      </div>
      {activeFilter && (
        <ModalManager
          isOpen={!!activeFilter}
          onClose={() => setActiveFilterKey(null)}
          title={activeFilter.modalTitle}
          customClass={styles.modalContent}
          modalHeaderClass={styles.modalHeader}
        >
          <SortModal
            options={activeFilter.options}
            multiSelect={activeFilter.multiSelect}
            withPriceRange={activeFilter.withPriceRange}
            maxPrice={
              activeFilter.withPriceRange ? activeFilter.maxPrice : undefined
            }
            defaultSelected={selectedMap[activeFilter.key] || []}
            onApply={(selectedValues) =>
              handleApply(activeFilter.key, selectedValues)
            }
            withClear={activeFilter.withClear}
            isApplying={applyingFilterKey === activeFilter.key}
          />
        </ModalManager>
      )}
    </div>
  );
}
